import React, { useState } from 'react';

interface Column {
  title: string;
  dataIndex: string;
  key: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, record: any, index: number) => React.ReactNode;
}

interface CustomTableProps {
  columns: Column[];
  data: any[];
  pageSize?: number;
}

const CustomTable: React.FC<CustomTableProps> = ({ columns, data, pageSize = 10 }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(data.length / pageSize);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const startIdx = (currentPage - 1) * pageSize;
  const endIdx = startIdx + pageSize;
  const pageData = data.slice(startIdx, endIdx);

  return (
    <div style={{ width: '100%', overflowX: 'auto' }}>
      <table
        className="custom-table"
        style={{
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: 0,
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          background: '#fff',
          marginBottom: 0,
        }}
      >
        <thead>
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                style={{
                  textAlign: col.align || 'left',
                  border: '1px solid #e0e0e0',
                  padding: '12px 10px',
                  background: '#f8f9ff',
                  fontWeight: 600,
                  color: '#8c8c8c',
                  fontSize: 14,
                  letterSpacing: 0.5,
                }}
              >
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pageData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center', padding: '16px' }}>
                No data
              </td>
            </tr>
          ) : (
            pageData.map((row, rowIndex) => (
              <tr
                key={row.key || row.id || rowIndex}
                className="custom-table-row"
              >
                {columns.map((col, colIndex) => (
                  <td
                    key={col.key + '-' + colIndex}
                    style={{
                      textAlign: col.align || 'left',
                      border: '1px solid #e0e0e0',
                      padding: '10px',
                      fontSize: 15,
                      color: '#262626',
                    }}
                  >
                    {col.render
                      ? col.render(row[col.dataIndex], row, startIdx + rowIndex)
                      : row[col.dataIndex]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {/* Pagination Controls */}
      <div
        style={{
          marginTop: 16,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          width: '100%',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="custom-pagination-btn"
            style={{
              borderRadius: 6,
              border: '1px solid #e0e0e0',
              background: currentPage === 1 ? '#f0f0f0' : '#fff',
              color: '#8c8c8c',
              padding: '6px 14px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              fontWeight: 500,
              marginRight: 2,
              transition: 'background 0.2s',
            }}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className="custom-pagination-btn"
              style={{
                fontWeight: page === currentPage ? 'bold' : 'normal',
                background: page === currentPage ? '#35b9e6' : '#fff',
                color: page === currentPage ? '#fff' : '#262626',
                borderRadius: 6,
                border: '1px solid #e0e0e0',
                padding: '6px 14px',
                margin: '0 2px',
                cursor: 'pointer',
                boxShadow: page === currentPage ? '0 2px 8px rgba(53,185,230,0.10)' : undefined,
                transition: 'background 0.2s',
              }}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="custom-pagination-btn"
            style={{
              borderRadius: 6,
              border: '1px solid #e0e0e0',
              background: currentPage === totalPages ? '#f0f0f0' : '#fff',
              color: '#8c8c8c',
              padding: '6px 14px',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              fontWeight: 500,
              marginLeft: 2,
              transition: 'background 0.2s',
            }}
          >
            Next
          </button>
        </div>
        <span style={{ color: '#8c8c8c', fontSize: 14, marginLeft: 16 }}>
          Showing {data.length === 0 ? 0 : startIdx + 1} - {Math.min(endIdx, data.length)} of {data.length}
        </span>
      </div>
    </div>
  );
};

export default CustomTable; 